import './RTUList.css'; // Make sure your CSS is properly set up for this

import { Card } from 'primereact/card';
import { FixedSizeList as List } from 'react-window';
import { RTU } from '../../../types/RTU';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getStatusColor, getStatusLabelKey } from '../../../utils/statusConfig';

// Compact item height for tighter list; keep in sync with the card's real
// rendered height (title + status/location row + cpu/disk row).
const ITEM_SIZE = 84;

interface RTUListProps {
  rtus: RTU[];
  currentRTU: RTU | undefined;
  setCurrentRTU: (currentRTU: RTU | undefined) => void;
  setHoveredRTU: (hoveredRTU: RTU | null) => void;
  totalCount: number;
  hasAppliedFilter: boolean;
}

// Only the fields the virtualized Row actually needs (totalCount/hasAppliedFilter
// are list-level, not per-row).
type RTURowData = Pick<RTUListProps, 'rtus' | 'currentRTU' | 'setCurrentRTU' | 'setHoveredRTU'>
  & { t: (key: string, options?: any) => string };

// This component will render each row in the list
const Row = ({ index, style, data }: { index: number; style: React.CSSProperties; data: RTURowData }) => {
  const { rtus, currentRTU, setCurrentRTU, setHoveredRTU, t } = data;
  const rtu = rtus[index];

  const navigate = useNavigate();

  const displayStatus = rtu.systemRestart === true ? 'rebooting' : rtu.status;
  const statusLabel = t(getStatusLabelKey(displayStatus), { defaultValue: String(displayStatus ?? '') });
  const statusColor = getStatusColor(displayStatus);

  const handleCardClick = () => {
    // Select the RTU to drive the map (item 3). Opening the detail page is a
    // separate, explicit action via the external-link button below.
    setCurrentRTU(rtu);
  };

  const handleOpenDetail = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/rtupage/${rtu.id}`, { state: { rtu } });
  };

  return (
    <div style={style}>
      <Card
        className={`rtu-card ${currentRTU?.id === rtu.id ? 'active-card' : ''}`}
        onClick={handleCardClick}
        onMouseEnter={() => setHoveredRTU(rtu)}
        onMouseLeave={() => setHoveredRTU(null)}
      >
        <div className="rtu-card-header">
          <span className="rtu-card-title">{rtu.name}</span>
          <button
            type="button"
            className="rtu-card-open-detail"
            title="Open RTU detail"
            aria-label="Open RTU detail"
            onClick={handleOpenDetail}
          >
            <i className="pi pi-external-link" />
          </button>
        </div>
        <div className="rtu-card-row">
          <span className="rtu-card-status">
            <span className="status-dot" style={{ backgroundColor: statusColor }}></span>
            {statusLabel}
          </span>
          <span className="rtu-card-location">{rtu.location}</span>
        </div>
        <div className="rtu-card-row rtu-card-metrics">
          <span>CPU: {Number.isFinite(rtu.cpuUsage) ? `${rtu.cpuUsage}%` : 'n/a'}</span>
          <span>Disk: {Number.isFinite(rtu.diskUsage) ? `${rtu.diskUsage}%` : 'n/a'}</span>
        </div>
      </Card>
    </div>
  );
};

const RTUList: React.FC<RTUListProps> = React.memo(({ rtus, currentRTU, setCurrentRTU, setHoveredRTU, totalCount, hasAppliedFilter }) => {
  const { t } = useTranslation();
  // Use the size of the container and item to calculate how many items to render
  const listHeight = window.innerHeight - 50 - 28; // Leave room for the summary row below

  return (
    <div style={{ height: window.innerHeight - 50 }}>
      <div className="rtu-list-summary">
        <span>
          Showing {rtus.length} of {totalCount} RTUs
        </span>
        {hasAppliedFilter && <span className="rtu-list-filtered-badge">Filtered</span>}
      </div>
      {
        rtus.length <= 0 ?
        <div className="rtu-list-empty">No RTUs match the current filter.</div>
        :
        <List
          height={listHeight}
          width={'100%'} // You might want to calculate this based on the parent container's width
          itemSize={ITEM_SIZE}
          itemCount={rtus.length}
          itemData={{ rtus, currentRTU, setCurrentRTU, setHoveredRTU, t }} // Pass down the props to the Row renderer
          style={{ overflowX: 'hidden' }}
        >
          {Row}
        </List>
      }
    </div>
  );
});

export default RTUList;
